import {factories} from '@strapi/strapi';

const UID = 'plugin::telegram-bot.telegram-user-activity';

export interface TelegramUserActivityParams {
  groupId: number;
  userId: number;
  messageCount?: number;
  timeSinceJoin?: number;
  isJoined?: boolean;
}

const TelegramUserActivityService = factories.createCoreService(UID, ({strapi}) => ({
  telegramGroup: strapi.plugin('telegram-bot').service('telegram-group'),
  telegramUser: strapi.plugin('telegram-bot').service('telegram-user'),
  async getActivity(groupId: number, telegramId: number) {
    // Get user activity with group telegram id and user telegram id
    const group = await this.telegramGroup.getGroupByTelegramId(groupId);
    if (!group) {
      return null;
    }

    const userInfo = await this.telegramUser.getUserByTelegramId(telegramId);
    if (!userInfo) {
      return null;
    }

    const existed = await strapi.documents(UID).findMany({
      limit: 1,
      filters: {
        user_id: {
          $eq: userInfo.id,
        },
        group_id: {
          $eq: group.id,
        },
      },
    });

    if (existed.length > 0) {
      const item = existed[0];
      if (!item.is_joined) {
        return null;
      }

      return {
        telegramId,
        groupId,
        messageCount: item.message_count || 0,
        timeSinceJoin: item.time_since_join,
      };
    }

    return null;
  },
  async getActivitiesByGroup(groupId: number) {
    return await strapi.documents(UID).findMany({
      populate: ['user_id'],
      filters: {
        group_id: {
          $eq: groupId,
        },
      }
    });
  },
  async upsertActivity(params: TelegramUserActivityParams) {
    const {groupId, userId, messageCount, timeSinceJoin, isJoined} = params;

    let timeSinceJoinDate = null;
    if (timeSinceJoin) {
      timeSinceJoinDate = new Date(timeSinceJoin);
    }

    const existed = await strapi.documents(UID).findMany({
      limit: 1,
      filters: {
        user_id: {
          $eq: userId,
        },
        group_id: {
          $eq: groupId,
        },
      },
    });

    if (existed.length > 0) {
      // Update user activity
      const userActivity = existed[0];
      if (messageCount) {
        userActivity.message_count = messageCount;
      }

      userActivity.is_joined = isJoined || false;

      await strapi.documents(UID).update({
        documentId: userActivity.documentId,
        data: userActivity as unknown
      });

      return userActivity;
    } else {

      // Create new user activity
      return await strapi.documents(UID).create({
        data: {
          user_id: userId,
          group_id: groupId,
          message_count: messageCount,
          time_since_join: timeSinceJoinDate,
          is_joined: isJoined || false,
        },
      });
    }
  },
  async updateMessageCount(groupId: number, userId: number) {
    const existed = await strapi.documents(UID).findMany({
      limit: 1,
      filters: {
        user_id: {
          $eq: userId,
        },
        group_id: {
          $eq: groupId,
        },
      },
    });

    if (existed.length > 0) {
      // Update user activity
      const userActivity = existed[0];

      await strapi.documents(UID).update({
        documentId: userActivity.documentId,
        data: {
          ...userActivity,
          // @ts-ignore
          message_count: userActivity.message_count + 1,
        }
      });
    } else {
      await this.upsertActivity({
        groupId,
        userId,
        messageCount: 1,
        timeSinceJoin: new Date().getTime(),
        isJoined: true,
      })
    }
  },
  async updateUserLeftGroup(groupId: number, telegramIds: number[]) {
    const data = await strapi.db.query(UID).findMany({
      filters: {
        group_id: {
          $eq: groupId,
        },
        user_id: {
          telegram_id: {
            $notIn: telegramIds
          }
        },
        is_joined: {
          $eq: true
        }
      },
    });
    const promises = data.map((item) => {
      return strapi.documents(UID).update({
        documentId: item.documentId,
        data: {
          ...item,
          is_joined: false,
        }
      });
    });
    await Promise.all(promises);
  },
  async updateActivity(documentId: string, params: Partial<TelegramUserActivityParams>) {
    await strapi.documents(UID).update({
      documentId,
      data: params as unknown
    });
  }
}));

export type TelegramUserActivityServiceType = typeof TelegramUserActivityService;

export default TelegramUserActivityService;
